import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Plus, Wrench, Ban, Paperclip, Truck, PackageCheck, Clock,
  Package, PackageSearch, Filter, FileDown, AlertTriangle, X, Image as ImgIcon,
  FileText, Loader2, CheckCircle2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import RetiradaFormulario from './RetiradaFormulario';
import ImagemSharePoint from '../../ImagemSharePoint';
import useEhCompacto from '../../../utils/useEhCompacto';
import useTelaMobile from '../../../utils/useTelaMobile';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';

// =====================================================================
// Logística › Retirada de Equipamento — A TELA DE ACOMPANHAMENTO.
//
// ⚠️⚠️ VEIO DE `manutencao/reparo/ReparoForms.js` em 2026-09-10, junto com o
//    módulo, e o desenho dela é o que sobreviveu a uma reescrita em uso (03/09)
//    — por isso ela virou a tela do Logística inteiro, e não a consulta mais
//    simples que a Retirada do T.I tinha. O que ganhou: a coluna e o filtro de
//    EQUIPE, e o botão do comprovante em PDF.
//
// ⚠️⚠️ A UNIDADE DESTA TELA É O ITEM, e "item" aqui quer dizer duas coisas
//    conforme a equipe da solicitação: a PEÇA (Manutenção) ou o próprio
//    EQUIPAMENTO retirado (T.I). As duas andam pela MESMA esteira de cinco
//    etapas, e é por isso que uma tela dá conta das duas.
//
// ⚠️⚠️ REESCRITA EM 2026-09-03, e o motivo é a devolutiva da Julia no primeiro
// uso: *"não consigo tirar nenhuma informação com ela"*. A versão anterior era
// uma lista de SOLICITAÇÕES (`loja · problema · N peças · data`) — e o técnico
// não acompanha solicitação, acompanha **PEÇA**: já foi encaminhada para o
// reparo? já voltou? está parada há quanto tempo? Nenhuma dessas perguntas
// tinha resposta na tela, porque o STATUS da peça não aparecia em lugar nenhum.
//
// A unidade desta tela é a PEÇA. A solicitação continua acessível (o problema, a
// foto, o cancelamento) na linha expandida.
//
// ⚠️ AQUI SE MONITORA, NÃO SE MOVE. Os passos do fluxo são do Compras e do
// Fiscal (`retirada-fluxo.js`); a Logística lê. O que ela controla é a abertura
// (o formulário), o comprovante em PDF e o cancelamento da própria solicitação.
//
// ⚠️ A CADEIA VEM DO `GET /fluxo` — a tela não tem cópia dos status nem da ordem
// deles. Só sabe pintá-los.
// =====================================================================

const API = '/api/logistica/retirada';
const COR = '#0e7490';
const COR_BORDA = '#cffafe';

// De quem é a solicitação.
//
// ⚠️⚠️ MAPA COM FALLBACK, e é o que faz esta tela deixar de misturar as duas
//    metades. Um ternário `EQUIPE === 'TI' ? … : …` funcionaria hoje e passaria
//    a rotular como "Manutenção" qualquer equipe nova. Equipe desconhecida sai
//    com o próprio nome, feia e visível.
const EQUIPES_SELO = {
  TI: { rotulo: 'T.I', cor: '#6b21a8', fundo: '#f3e8ff' },
  Manutencao: { rotulo: 'Manutenção', cor: '#b45309', fundo: '#fef3c7' },
};

const SeloEquipe = ({ equipe }) => {
  const e = EQUIPES_SELO[equipe] || { rotulo: equipe || '—', cor: '#64748b', fundo: '#f1f5f9' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 800, color: e.cor, background: e.fundo,
      whiteSpace: 'nowrap',
    }}>{e.rotulo}</span>
  );
};
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const CANCELADA = 'Cancelada';

// Só a aparência. ⚠️ O que cada etapa SIGNIFICA vem da API (`descricao`).
const ESTILO = {
  'Pendente': { bg: '#f1f5f9', color: '#475569' },
  'Enviado para Fiscal': { bg: '#fef3c7', color: '#92400e' },
  'Enviado para Reparo': { bg: '#dbeafe', color: '#1d4ed8' },
  'Retorno do Reparo': { bg: '#d1fae5', color: '#065f46' },
  'Finalizado': { bg: '#e0e7ff', color: '#3730a3' },
  'Cancelada': { bg: '#e2e8f0', color: '#64748b' },
};
const estiloDe = (s) => ESTILO[s] || ESTILO.Pendente;

// ⚠️⚠️ AS TRÊS ABAS SÃO A NAVEGAÇÃO DA TELA (decisão da Julia, 03/09: *"podemos
// simplificar em 3"*). Elas não são status — são AGRUPAMENTOS de status, na
// linguagem da pergunta que o técnico faz: *já saiu? está fora? voltou?*
//
// A primeira versão desta tela tinha TRÊS navegações para a mesma coisa (três
// blocos grandes + seis pastilhas de status + a barra de filtros). Três jeitos
// de chegar ao mesmo recorte não é flexibilidade, é a pessoa não sabendo qual
// usar — e o status exato de cada peça já está na linha dela.
//
// ⚠️ Etapa nova no fluxo entra no balde certo por estar em uma destas listas. Se
// alguém acrescentar uma etapa no `reparo-fluxo.js` e esquecer daqui, ela cai em
// 'outro' e o item **não aparece em aba nenhuma** — por isso existe a trava
// visual do rodapé ("N item(ns) fora das abas"), que denuncia em vez de esconder.
// ⚠️ 'Devolutiva Fiscal' SAIU da lista em 03/09: o status foi fundido ao 'Envio
// para Manutenção', e o que o Fiscal faz virou complemento (a nota é informada
// dentro de 'Enviado para Fiscal', sem mover o andamento). Peça esperando a nota
// continua aqui — que é o certo: ela ainda não saiu.
const NA_ESTEIRA = ['Pendente', 'Enviado para Fiscal'];                      // ainda não saiu
// ⚠️ `Aguardando Retorno` SAIU da lista em 10/09: o status foi fundido ao
//    `Enviado para Reparo` (a previsão passou a ser pedida no mesmo ato do
//    envio). Item com o texto antigo cai em 'outro' e é ANUNCIADO no rodapé —
//    a migration `-10c` converte os que existiam.
const FORA = ['Enviado para Reparo'];                                 // saiu e não voltou
const VOLTOU = ['Retorno do Reparo'];                               // de volta no Compras, aguardando o Finalizar
// ⚠️ `Finalizado` (DEM-00016, 28/09): o fim ÚNICO dos três caminhos. Item de
//    ESTOQUE cai direto aqui (Pendente→Finalizado, sem passar por fora/voltou);
//    normal e interno chegam pelo passo "Finalizar" depois do retorno. Sem esta
//    lista, todos cairiam em 'outro' e a tela os anunciaria como status órfão.
const FINALIZADO = ['Finalizado'];                                      // encerrado

const ABAS = [
  { chave: 'esteira', rotulo: 'Ainda não saíram', descricao: 'aguardando o Compras lançar no SAP, o Fiscal emitir a nota ou o envio ao fornecedor' },
  { chave: 'fora', rotulo: 'No reparo', descricao: 'encaminhadas ao fornecedor e ainda não devolvidas' },
  { chave: 'voltou', rotulo: 'Já voltaram', descricao: 'de volta no Compras, aguardando o Compras finalizar' },
  { chave: 'finalizado', rotulo: 'Finalizadas', descricao: 'encerradas: voltaram do reparo ou ficaram no estoque do escritório' },
];

// ⚠️ Puras e FORA do componente de propósito: dependem só da peça. Dentro do
// corpo do componente elas seriam recriadas a cada render, e aí ou entram nas
// dependências de todo `useMemo` (recalculando tudo sempre, o que anula o memo)
// ou o lint acusa dependência faltando para sempre.
//
// Uma peça está VIVA quando nem ela nem a solicitação dela foram canceladas. Os
// placares só contam vivas — número que soma cancelado faz o técnico procurar
// peça que ninguém vai entregar.
const viva = (p) => !p.SOLIC_CANCELADA_EM && p.STATUS !== CANCELADA;

const grupoDe = (p) => {
  if (!viva(p)) return 'cancelada';
  if (FORA.includes(p.STATUS)) return 'fora';
  if (VOLTOU.includes(p.STATUS)) return 'voltou';
  if (FINALIZADO.includes(p.STATUS)) return 'finalizado';
  if (NA_ESTEIRA.includes(p.STATUS)) return 'esteira';
  return 'outro';
};

const soDia = (v) => (v ? String(v).slice(0, 10) : null);
const dataBR = (v) => (soDia(v) ? soDia(v).split('-').reverse().join('/') : '—');
const num = { fontVariantNumeric: 'tabular-nums' };
const moeda = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const qtd = (v) => (Number(v) % 1 === 0 ? String(Number(v)) : Number(v).toFixed(2).replace('.', ','));

// Dias de calendário entre duas datas (ou de uma até hoje). ⚠️ Dias CORRIDOS:
// o que se mede é o tempo que a loja passou sem a peça, e ele não faz pausa no
// fim de semana. Mesma decisão do "parado há" da Certificação.
const diasEntre = (de, ate) => {
  const a = soDia(de); if (!a) return null;
  const b = soDia(ate) || new Date().toISOString().slice(0, 10);
  return Math.max(0, Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000));
};

// Cor do tempo. Os cortes são os mesmos da fila da Certificação (14/30 dias),
// para as duas telas do módulo não medirem "demora" com réguas diferentes.
const corTempo = (d) => (d > 30 ? '#b91c1c' : d > 14 ? '#b45309' : '#64748b');

const hojeISO = () => new Date().toISOString().slice(0, 10);

// ⚠️⚠️ O ATRASO PASSOU A SER FATO, E NÃO RÉGUA NOSSA (03/09). Desde que a etapa
// "Enviado para Reparo" exige a **previsão de retorno**, "este item está
// atrasada?" tem resposta objetiva: passou da data que o FORNECEDOR prometeu.
//
// ⚠️ O corte de 14 dias NÃO saiu — virou RESERVA, para a peça que não tem
// previsão (as que passaram pela etapa antes da coluna existir). Sem a reserva,
// justamente essas nunca pareceriam atrasadas.
const atrasadaNaPrevisao = (p) => !!p.DATA_PREVISAO_RETORNO && !p.DATA_RETORNO_COMPRAS
  && soDia(p.DATA_PREVISAO_RETORNO) < hojeISO();

// Dias além do prometido (só faz sentido com previsão e sem retorno).
const diasAlemDaPrevisao = (p) => (atrasadaNaPrevisao(p)
  ? diasEntre(p.DATA_PREVISAO_RETORNO, null) : null);

// Tempo que o equipamento passou aguardando para SAIR da loja: da solicitação
// (CRIADO_EM) até o lançamento no SAP (DATA_SOLICITACAO_SAP), que é o que libera a
// retirada. ⚠️ Se ainda não foi lançado no SAP, `diasEntre` conta até hoje — o
// equipamento CONTINUA esperando, e é justamente esse número que a loja sente.
const esperaRetirada = (p) => diasEntre(p.CRIADO_EM, p.DATA_SOLICITACAO_SAP);

// A peça voltou DEPOIS do prometido? É a medida da palavra do fornecedor —
// e só existe quando as duas datas existem.
const voltouAtrasada = (p) => !!p.DATA_PREVISAO_RETORNO && !!p.DATA_RETORNO_COMPRAS
  && soDia(p.DATA_RETORNO_COMPRAS) > soDia(p.DATA_PREVISAO_RETORNO);

const th = { padding: '11px 14px', borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap', textAlign: 'left' };
const td = { padding: '11px 14px', fontSize: 13.5, color: '#0f172a', verticalAlign: 'top' };
const sel = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#475569', background: '#fff' };

// ---------------------------------------------------------------------
// Card de indicador de uma aba: o número grande + o que ELE conta que a aba não
// conta (o alarme, o tempo médio, a espera).
//
// ⚠️ CLICAR NO CARD TROCA A ABA — os dois controlam o MESMO estado. Não são duas
// navegações: é uma, com um resumo que também é atalho. Foi o desenho encontrado
// depois de duas idas e voltas (03/09): a 1ª versão tinha card + pastilha de
// status + filtro, três recortes competindo; a 2ª tirou os cards e perdeu os
// indicadores. O card mede, a aba localiza.
// ---------------------------------------------------------------------
function CardIndicador({ tom, icone: Icone, rotulo, valor, indicador, ativo, onClick, compacto }) {
  return (
    <button type="button" onClick={onClick} title={`Ver ${rotulo.toLowerCase()}`}
      style={{
        flex: compacto ? '1 1 45%' : '1 1 0', minWidth: compacto ? 0 : 190, textAlign: 'left',
        background: ativo ? tom.bg : '#fff',
        border: `${ativo ? 2 : 1}px solid ${ativo ? tom.color : '#e0e7ff'}`,
        borderRadius: 12, padding: ativo ? '13px 15px' : '14px 16px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 6,
        transition: 'background .15s, border-color .15s',
      }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800,
        letterSpacing: .3, textTransform: 'uppercase', color: tom.color,
      }}>
        <Icone size={14} /> {rotulo}
      </span>
      <span style={{ ...num, fontSize: 30, fontWeight: 800, color: tom.color, lineHeight: 1 }}>{valor}</span>
      {/* O indicador é o que impede o número de ser lido como "está tudo bem":
          14 peças no reparo com 9 atrasadas é outra história que 14 recentes. */}
      <span style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4, minHeight: 32 }}>{indicador}</span>
    </button>
  );
}

const RetiradaAcompanhamento = ({ onVoltar, rotuloVoltar }) => {
  const compacto = useEhCompacto();
  const { ehMobile } = useTelaMobile();
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const perfil = perfilNoModulo(user?.perfil, 'logistica') || '';
  // Quem pode CANCELAR a solicitação inteira (espelha o backend). ⚠️ Quem abriu
  // também pode, e isso o servidor decide sozinho pelo `USUARIO_ID` — a tela não
  // tem como saber, então o botão aparece para a gestão e o autor descobre pelo
  // 403 se tentar em pedido alheio. Alargar a lista aqui não daria poder nenhum:
  // a porta que vale é a de lá.
  const podeGerir = ['Admin', 'Logistica', 'Manutencao', 'Diretoria', 'Gerente TI'].includes(perfil);

  const [tela, setTela] = useState('lista');   // lista | nova
  const [pecas, setPecas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [fLoja, setFLoja] = useState('');
  const [fEquipe, setFEquipe] = useState('');
  const [gerandoPdf, setGerandoPdf] = useState(null);   // o ID da solicitação
  // A aba ABRE em "No reparo": é a peça que a loja está esperando, e é o que o
  // técnico veio ver. Abrir em "Ainda não saíram" mostraria primeiro o que ainda
  // não é problema dele.
  const [aba, setAba] = useState('fora');
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      // ⚠️ `canceladas=1`: aqui o cancelado é RASTRO e precisa aparecer —
      // esconder faz a loja reabrir o mesmo pedido achando que o dela não gravou.
      //
      // ⚠️ Esta tela NÃO pede mais o `GET /fluxo`: com a navegação em três abas,
      // ela não desenha etapa por etapa. Os status que compõem cada aba estão
      // nas listas do topo deste arquivo — e a trava do rodapé ("peça em um
      // status que esta tela ainda não agrupa") é o que denuncia se o fluxo
      // ganhar uma etapa e estas listas ficarem atrás. A tela do COMPRAS, que
      // opera o fluxo, continua lendo a cadeia da API.
      const rp = await fetch(`${API}/itens?canceladas=1`, { headers: auth() });
      if (!rp.ok) throw new Error(`HTTP ${rp.status}`);
      setPecas(await rp.json());
    } catch {
      setErro('Erro ao carregar os itens.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const lojas = useMemo(() => [...new Set(pecas.map((p) => p.LOJA).filter(Boolean))].sort(), [pecas]);
  const equipes = useMemo(() => [...new Set(pecas.map((p) => p.EQUIPE).filter(Boolean))].sort(), [pecas]);

  const placar = useMemo(() => {
    // As chaves são as das ABAS — é o mesmo `grupoDe` que filtra a lista, então
    // a contagem da aba e o que ela mostra não podem divergir.
    const c = {
      fora: 0, esteira: 0, voltou: 0, finalizado: 0, cancelada: 0, outro: 0,
      atrasadas: 0, atrasadasPorPrevisao: 0, semPrevisao: 0, voltaramAtrasadas: 0,
      finalizadoReparo: 0, finalizadoEstoque: 0,
    };
    pecas.forEach((p) => {
      const g = grupoDe(p);
      c[g] = (c[g] || 0) + 1;
      // O ALARME da tela. ⚠️ Com previsão gravada, "atrasada" é ter passado da
      // data prometida — fato, não régua nossa. Sem previsão (peça anterior à
      // coluna), cai na reserva do corte de 14 dias.
      if (g === 'fora') {
        if (p.DATA_PREVISAO_RETORNO) {
          if (atrasadaNaPrevisao(p)) { c.atrasadas += 1; c.atrasadasPorPrevisao += 1; }
        } else {
          const d = diasEntre(p.DATA_ENVIO_MANUTENCAO, null);
          if (d !== null && d > 14) { c.atrasadas += 1; c.semPrevisao += 1; }
        }
      }
      // Quantas voltaram fora do prazo — a medida da palavra do fornecedor.
      if (g === 'voltou' && voltouAtrasada(p)) c.voltaramAtrasadas += 1;
      // Encerradas, separando quem voltou do reparo de quem ficou no estoque
      // (o caminho conta a história do encerramento).
      if (g === 'finalizado') {
        if (p.CAMINHO === 'estoque') c.finalizadoEstoque += 1; else c.finalizadoReparo += 1;
      }
    });
    return c;
  }, [pecas]);

  // ---------------------------------------------------------------
  // Os indicadores dos cards. Cada um responde o que o CONTADOR não responde.
  // ---------------------------------------------------------------
  // A peça mais tempo fora — é o número que o técnico leva para a reunião.
  const piorFora = useMemo(() => {
    let pior = null;
    pecas.forEach((p) => {
      if (grupoDe(p) !== 'fora') return;
      const d = diasEntre(p.DATA_ENVIO_MANUTENCAO, null);
      if (d !== null && (pior === null || d > pior)) pior = d;
    });
    return pior;
  }, [pecas]);

  // O pior ATRASO contra o prometido. ⚠️ É outro número do "mais tempo fora":
  // uma peça de reparo longo pode estar fora há 40 dias e DENTRO do prazo.
  const piorAtraso = useMemo(() => {
    let pior = null;
    pecas.forEach((p) => {
      if (grupoDe(p) !== 'fora') return;
      const d = diasAlemDaPrevisao(p);
      if (d !== null && (pior === null || d > pior)) pior = d;
    });
    return pior;
  }, [pecas]);

  // Quanto tempo a peça está esperando para SAIR (aberta e ainda na esteira).
  // ⚠️ Conta da ABERTURA da solicitação, não do último passo: o que a loja sente
  // é o tempo desde que pediu — se o atraso está no Compras ou no Fiscal é
  // detalhe interno, e a esteira inteira é responsabilidade do portal.
  const esperaEsteira = useMemo(() => {
    const dias = pecas.filter((p) => grupoDe(p) === 'esteira')
      .map((p) => diasEntre(p.CRIADO_EM, null)).filter((d) => d !== null);
    if (!dias.length) return null;
    return { pior: Math.max(...dias), acima7: dias.filter((d) => d > 7).length };
  }, [pecas]);

  // Tempo médio de reparo do que JÁ VOLTOU — é o único número desta tela que
  // mede o FORNECEDOR, e o que dá base para cobrar prazo.
  // ⚠️ Só entra peça com as DUAS datas: média com denominador incompleto
  // parece precisa e não é. E vem com a contagem colada, pelo mesmo motivo do
  // "N de M" do painel de tempos da Certificação.
  const mediaReparo = useMemo(() => {
    const dias = pecas.filter((p) => grupoDe(p) === 'voltou')
      .map((p) => diasEntre(p.DATA_ENVIO_MANUTENCAO, p.DATA_RETORNO_COMPRAS))
      .filter((d) => d !== null);
    if (!dias.length) return null;
    return { media: Math.round(dias.reduce((a, b) => a + b, 0) / dias.length), n: dias.length };
  }, [pecas]);

  // O dinheiro que está fora de casa: soma do valor das peças em reparo.
  // ⚠️ Só soma peça COM valor lançado, e diz quantas ficaram fora da conta —
  // total que ignora peça sem valor se lê como o total de tudo.
  const valorFora = useMemo(() => {
    const emReparo = pecas.filter((p) => grupoDe(p) === 'fora');
    const comValor = emReparo.filter((p) => p.VALOR_UNITARIO != null);
    if (!comValor.length) return null;
    return {
      total: comValor.reduce((a, p) => a + Number(p.VALOR_UNITARIO) * Number(p.QUANTIDADE), 0),
      semValor: emReparo.length - comValor.length,
    };
  }, [pecas]);

  const filtrada = useMemo(() => pecas.filter((p) => {
    if (fEquipe && p.EQUIPE !== fEquipe) return false;
    if (fLoja && p.LOJA !== fLoja) return false;
    return grupoDe(p) === aba;
  }).sort((a, b) => {
    // Dentro da aba, o MAIS TEMPO fora no topo: é a ordem em que se cobra o
    // fornecedor. Peça sem data de envio (a esteira) cai para o fim.
    const da = diasEntre(a.DATA_ENVIO_MANUTENCAO, a.DATA_RETORNO_COMPRAS) ?? -1;
    const db = diasEntre(b.DATA_ENVIO_MANUTENCAO, b.DATA_RETORNO_COMPRAS) ?? -1;
    return db - da || b.ID - a.ID;
  }), [pecas, fEquipe, fLoja, aba]);

  // Peça que não caiu em nenhuma aba (status novo no fluxo sem lugar aqui).
  // ⚠️ Contada e ANUNCIADA: peça invisível na tela do técnico é pior do que
  // aba feia.
  const semAba = useMemo(() => pecas.filter((p) => grupoDe(p) === 'outro'), [pecas]);

  /**
   * Abre o comprovante em PDF da SOLICITAÇÃO (não do item — o documento é um só,
   * e no T.I é ele que acompanha o aparelho na porta).
   *
   * ⚠️⚠️ NÃO PODE SER UM `<a href>` COMUM, e já foi — o primeiro teste em uso
   *    devolveu **"Acesso negado. Nenhum token fornecido"**. O JWT vive no
   *    `localStorage` e é o `fetch` que o anexa no header; o navegador **não**
   *    manda header nenhum ao seguir um link. Rota autenticada só se alcança por
   *    `fetch`. (Mesma classe de erro que a foto do SharePoint e o `<video>` do
   *    procedimento já custaram ao portal.)
   *
   * ⚠️⚠️ NO CELULAR É `<a download>` E NO COMPUTADOR `window.open`, e a diferença
   *    não é gosto: gerar o PDF leva SEGUNDOS (Puppeteer + foto do SharePoint), e
   *    quando o `window.open` finalmente roda o crédito do toque do usuário já
   *    expirou — Safari/iOS e Chrome/Android bloqueiam como popup, **em
   *    silêncio**. O `<a download>` não é tratado como popup e passa.
   * ⚠️ E SEM `noopener` no `window.open`: com ele o contexto novo se desliga da
   *    origem que criou o blob, e Safari/Firefox recusam carregar `blob:` ali.
   * ⚠️ O `<a>` entra no documento ANTES do clique: o Firefox ignora clique
   *    programático em elemento solto na memória.
   * ⚠️ A URL do blob é liberada DEPOIS de um tempo: revogar na hora fecharia o
   *    PDF antes de o navegador terminar de desenhá-lo.
   */
  const abrirPdf = async (idSolicitacao) => {
    setGerandoPdf(idSolicitacao); setErro('');
    try {
      const r = await fetch(`${API}/${idSolicitacao}/pdf`, { headers: auth() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || 'Não foi possível gerar o comprovante.');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (ehMobile) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Retirada_${idSolicitacao}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setErro(e.message);
    } finally {
      setGerandoPdf(null);
    }
  };

  // ---------------------------------------------------------------------
  // Abre a NOTA FISCAL ou o TXT anexados.
  //
  // ⚠️ Passa pelo `abrirArquivoComToken` porque o proxy `/documento` é rota
  // AUTENTICADA: o navegador não manda o `Authorization` ao seguir um `<a href>`
  // e o clique voltava 401 numa aba nova (quebrado de 03/09 a 16/09/2026). O
  // helper ainda resolve o que um `window.open` cru erra — aba bloqueada no
  // celular vira download, e o `blob:` não perde a origem que o criou.
  // ⚠️ O nome padrão leva a EXTENSÃO certa: sem ela, o Windows abre o TXT no
  // programa errado quando o servidor não manda `Content-Disposition`.
  // ---------------------------------------------------------------------
  const abrirAnexo = async (url, nomePadrao) => {
    setErro('');
    try {
      await abrirArquivoComToken(`${API}/documento?url=${encodeURIComponent(url)}`, nomePadrao);
    } catch (e) {
      setErro(e.message);
    }
  };

  const cancelarSolicitacao = async (p) => {
    // eslint-disable-next-line no-alert
    const motivo = window.prompt(`Cancelar a solicitação #${p.ID_REPARO} inteira (e todos os itens dela)? Diga o motivo:`);
    if (motivo === null) return;
    setErro('');
    try {
      const r = await fetch(`${API}/${p.ID_REPARO}/cancelar`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.message || 'Não foi possível cancelar.');
      setDetalhe(null);
      await carregar();
    } catch (e) { setErro(e.message); }
  };

  const exportar = () => {
    const linhas = filtrada.map((p) => ({
      'Item': p.ID, 'Solicitação': p.ID_REPARO, 'Equipe': p.EQUIPE, 'Loja': p.LOJA,
      'Caminho': p.CAMINHO || 'normal',
      'Produto': p.DESCRICAO, 'Código': p.CODIGO || '',
      // Vazio nas peças (não têm série) e nas retiradas anteriores a 14/09.
      'Número de série': p.NUMERO_SERIE || '',
      'Origem': p.ORIGEM === 'Central' ? 'Central de Pedidos' : 'Fora da Central',
      'Quantidade': Number(p.QUANTIDADE),
      'Situação': p.SOLIC_CANCELADA_EM ? 'Solicitação cancelada' : p.STATUS,
      'Solicitação SAP': p.NUMERO_SOLICITACAO_SAP || '',
      'Nota fiscal': p.NUMERO_NOTA_FISCAL || '',
      'Valor total': p.VALOR_UNITARIO == null ? '' : Number(p.VALOR_UNITARIO) * Number(p.QUANTIDADE),
      'Encaminhada em': dataBR(p.DATA_ENVIO_MANUTENCAO),
      'Rastreio': p.CODIGO_RASTREIO || '',
      'Previsão de retorno': dataBR(p.DATA_PREVISAO_RETORNO),
      'Atrasada?': atrasadaNaPrevisao(p) ? `SIM (${diasAlemDaPrevisao(p)}d)` : '',
      'Voltou fora do prazo?': voltouAtrasada(p) ? 'SIM' : '',
      'Voltou em': dataBR(p.DATA_RETORNO_COMPRAS),
      // Dias fora: fechado quando voltou, corrido (até hoje) quando não.
      'Dias fora': diasEntre(p.DATA_ENVIO_MANUTENCAO, p.DATA_RETORNO_COMPRAS) ?? '',
      'Ainda fora?': grupoDe(p) === 'fora' ? 'SIM' : '',
      'Problema': p.DESCRICAO_PROBLEMA,
      'Fornecedor do reparo': p.FORNECEDOR_REPARO || '',
      'Aberta em': dataBR(p.CRIADO_EM), 'Aberta por': p.SOLICITANTE || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), 'Reparos');
    XLSX.writeFile(wb, `ReparoPecas_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (tela === 'nova') {
    return (
      <RetiradaFormulario
        onVoltar={() => setTela('lista')}
        onGravado={async () => { setTela('lista'); await carregar(); }}
      />
    );
  }

  return (
    <div>
      {onVoltar && (
        <button onClick={onVoltar}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none',
            background: 'transparent', color: '#64748b', fontWeight: 600,
            fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12,
          }}>
          <ArrowLeft size={16} /> {rotuloVoltar || 'Voltar'}
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 200, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Solicitação de Retirada de equipamento
        </h3>
        {filtrada.length > 0 && (
          <button onClick={exportar} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <FileDown size={16} /> Exportar Excel
          </button>
        )}
        <button onClick={() => setTela('nova')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', background: COR, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
          <Plus size={16} /> Nova solicitação
        </button>
      </div>
      <p style={{ margin: '0 0 16px 0', fontSize: 12.5, color: '#94a3b8' }}>
        Onde está cada item que saiu das unidades — o equipamento retirado pela T.I e a
        peça pedida pela Manutenção. O andamento é registrado pelo Compras e pelo Fiscal;
        aqui você acompanha e reimprime o comprovante.
      </p>

      {/* ---------------- OS CARDS DE INDICADOR ----------------
          Um por aba. Clicar troca a aba (mesmo estado) — o card mede, a aba
          localiza. */}
      {!carregando && pecas.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          <CardIndicador
            tom={{ bg: '#fef3c7', color: '#92400e' }} icone={Clock} compacto={compacto}
            rotulo="Ainda não saíram" valor={placar.esteira}
            ativo={aba === 'esteira'} onClick={() => setAba('esteira')}
            indicador={esperaEsteira
              ? <>a mais antiga espera há <strong style={{ color: corTempo(esperaEsteira.pior) }}>{esperaEsteira.pior} dias</strong>
                {esperaEsteira.acima7 > 0 ? ` · ${esperaEsteira.acima7} há mais de 7` : ''}</>
              : 'nada parado na esteira'}
          />
          <CardIndicador
            tom={{ bg: '#dbeafe', color: '#1d4ed8' }} icone={Truck} compacto={compacto}
            rotulo="No reparo" valor={placar.fora}
            ativo={aba === 'fora'} onClick={() => setAba('fora')}
            indicador={placar.fora === 0 ? 'nada no fornecedor' : (
              <>
                {/* ⚠️ ATRASADA = passou da data que o fornecedor prometeu. O
                    "há mais de 14 dias" só aparece para peça SEM previsão, e
                    dizendo que é por falta dela — misturar as duas contas faria
                    o número não significar nada. */}
                {placar.atrasadasPorPrevisao > 0 && (
                  <><strong style={{ color: '#b91c1c' }}>{placar.atrasadasPorPrevisao} passaram da previsão</strong>
                    {piorAtraso !== null ? ` (a pior, há ${piorAtraso} dias)` : ''}</>
                )}
                {placar.semPrevisao > 0 && (
                  <>{placar.atrasadasPorPrevisao > 0 ? ' · ' : ''}
                    <strong style={{ color: '#b45309' }}>{placar.semPrevisao} sem previsão</strong> há mais de 14 dias</>
                )}
                {placar.atrasadas === 0 && <>todas dentro do prazo · a mais antiga saiu há {piorFora} dias</>}
                {valorFora && <> · <strong>{moeda(valorFora.total)}</strong> fora de casa
                  {valorFora.semValor > 0 ? ` (${valorFora.semValor} sem valor lançado)` : ''}</>}
              </>
            )}
          />
          <CardIndicador
            tom={{ bg: '#d1fae5', color: '#065f46' }} icone={PackageCheck} compacto={compacto}
            rotulo="Já voltaram" valor={placar.voltou}
            ativo={aba === 'voltou'} onClick={() => setAba('voltou')}
            indicador={mediaReparo
              ? <>o reparo levou <strong>{mediaReparo.media} {mediaReparo.media === 1 ? 'dia' : 'dias'}</strong> em média
                {mediaReparo.n !== placar.voltou ? ` (em ${mediaReparo.n} de ${placar.voltou})` : ''}
                {/* Cumpriu o que prometeu? É a leitura que cobra o fornecedor
                    na renovação, e só existe porque a previsão é registrada. */}
                {placar.voltaramAtrasadas > 0
                  ? <> · <strong style={{ color: '#b45309' }}>{placar.voltaramAtrasadas} fora do prazo</strong></>
                  : <> · todas no prazo prometido</>}</>
              : 'nada devolvido ainda'}
          />
          {/* Encerradas: o número grande + a quebra reparo × estoque, que é a
              história que o total sozinho não conta (estoque nunca foi a reparo). */}
          <CardIndicador
            tom={{ bg: '#e0e7ff', color: '#3730a3' }} icone={CheckCircle2} compacto={compacto}
            rotulo="Finalizadas" valor={placar.finalizado}
            ativo={aba === 'finalizado'} onClick={() => setAba('finalizado')}
            indicador={placar.finalizado === 0 ? 'nada encerrado ainda'
              : <>
                {placar.finalizadoReparo > 0 && <><strong>{placar.finalizadoReparo}</strong> do reparo</>}
                {placar.finalizadoReparo > 0 && placar.finalizadoEstoque > 0 ? ' · ' : ''}
                {placar.finalizadoEstoque > 0 && <><strong>{placar.finalizadoEstoque}</strong> ficaram no estoque</>}
              </>}
          />
        </div>
      )}

      {/* ---------------- AS TRÊS ABAS ----------------
          A navegação da tela, em vez de três recortes competindo (era bloco +
          pastilha de status + filtro). O status exato de cada peça continua na
          linha dela — a aba é a pergunta, a linha é a resposta. */}
      {!carregando && pecas.length > 0 && (
        <>
          <div style={{
            display: 'flex', gap: 4, marginBottom: 10, borderBottom: '1px solid #e0e7ff',
            flexWrap: 'wrap',
          }}>
            {ABAS.map((a) => {
              const ativa = aba === a.chave;
              const n = placar[a.chave] || 0;
              return (
                <button key={a.chave} type="button" onClick={() => setAba(a.chave)}
                  title={a.descricao}
                  style={{
                    padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                    fontWeight: ativa ? 800 : 600, fontSize: 14,
                    color: ativa ? COR : '#64748b',
                    borderBottom: `2px solid ${ativa ? COR : 'transparent'}`, marginBottom: -1,
                    whiteSpace: 'nowrap',
                  }}>
                  {a.rotulo}{' '}
                  <span style={{ ...num, color: ativa ? COR : '#94a3b8' }}>({n})</span>
                </button>
              );
            })}
            {/* A aba das canceladas só existe quando existe cancelada — ela é
                rastro, não fluxo, e uma aba "(0)" permanente só ocupa espaço.
                Mas esconder a existente faria a loja reabrir o mesmo pedido. */}
            {placar.cancelada > 0 && (
              <button type="button" onClick={() => setAba('cancelada')}
                style={{
                  padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                  fontWeight: aba === 'cancelada' ? 800 : 600, fontSize: 14,
                  color: aba === 'cancelada' ? '#475569' : '#94a3b8',
                  borderBottom: `2px solid ${aba === 'cancelada' ? '#94a3b8' : 'transparent'}`,
                  marginBottom: -1, marginLeft: 'auto', whiteSpace: 'nowrap',
                }}>
                Canceladas <span style={num}>({placar.cancelada})</span>
              </button>
            )}
          </div>

          {/* O que a aba É — só isso. ⚠️ O ALARME saiu daqui quando os cards
              voltaram: ele mora no card, e repetir "N há mais de 14 dias" nas
              duas linhas faz o olho parar de ler as duas. */}
          <p style={{ margin: '0 0 14px 0', fontSize: 12.5, color: '#64748b' }}>
            {aba === 'cancelada'
              ? 'pedidos desfeitos — ficam aqui como rastro da decisão'
              : (ABAS.find((a) => a.chave === aba) || {}).descricao}
          </p>

          {/* ⚠️ Peça que não caiu em aba nenhuma é ANUNCIADA, não escondida: é o
              sintoma de etapa nova no fluxo sem lugar nesta tela. */}
          {semAba.length > 0 && (
            <p style={{ margin: '-6px 0 14px 0', fontSize: 12, color: '#b45309' }}>
              ⚠️ {semAba.length} item(ns) em um status que esta tela ainda não agrupa
              ({[...new Set(semAba.map((x) => x.STATUS))].join(', ')}) — avise a equipe de dados.
            </p>
          )}
        </>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e0e7ff', borderRadius: 12, padding: 12, marginBottom: 14 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 700, fontSize: 13 }}><Filter size={15} /> Filtros</span>
        {/* ⚠️ O filtro de equipe SÓ APARECE quando as duas existem na lista: um
            `<select>` com uma opção é ruído, e antes de a primeira solicitação
            de uma delas chegar ele sugeriria uma divisão que ainda não existe. */}
        {equipes.length > 1 && (
          <select value={fEquipe} onChange={(e) => setFEquipe(e.target.value)} style={sel}>
            <option value="">T.I e Manutenção</option>
            {equipes.map((e) => (
              <option key={e} value={e}>{`Só ${(EQUIPES_SELO[e] || {}).rotulo || e}`}</option>
            ))}
          </select>
        )}
        <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={sel}>
          <option value="">Todas as lojas</option>
          {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        {/* ⚠️ Os DOIS filtros entram na condição que mostra o botão E no que ele
            limpa. Faltando o primeiro, o filtro de equipe fica ativo sem o
            "Limpar" à vista; faltando o segundo, o botão aparece e não limpa
            tudo. É filtro ativo e invisível: a lista parece vazia sem motivo. */}
        {(fLoja || fEquipe) && (
          <button onClick={() => { setFLoja(''); setFEquipe(''); }}
            style={{ padding: '8px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Limpar
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94a3b8', ...num }}>
          {filtrada.length} de {pecas.length} itens
        </span>
      </div>

      {erro && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: 13.5, fontWeight: 600 }}>
          <AlertTriangle size={16} /> {erro}
          <button onClick={() => setErro('')} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: '#991b1b' }}><X size={15} /></button>
        </div>
      )}

      {carregando ? (
        <p style={{ color: '#64748b' }}>Carregando…</p>
      ) : pecas.length === 0 ? (
        <div style={{ padding: 36, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          <Wrench size={26} color={COR} />
          <p style={{ fontWeight: 700, color: '#0f172a', margin: '12px 0 4px 0' }}>Nenhuma solicitação ainda</p>
          <p style={{ fontSize: 13.5, margin: 0 }}>Use "Nova solicitação" para registrar a retirada de um equipamento ou pedir peças.</p>
        </div>
      ) : filtrada.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhum item para os filtros escolhidos.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: compacto ? 760 : 1000 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={th}>Equipe</th>
                  <th style={th}>Loja</th>
                  <th style={th}>Item</th>
                  <th style={{ ...th, textAlign: 'right' }}>Qtd</th>
                  <th style={th}>Onde está</th>
                  <th style={th}>Encaminhada</th>
                  <th style={th}>Voltou</th>
                  {/* ⚠️ Na esteira o equipamento ainda não saiu, então "Fora há" não
                      faz sentido: a coluna vira "Aguardando retirada" (solicitação → SAP). */}
                  <th style={{ ...th, textAlign: 'right' }}>{aba === 'esteira' ? 'Aguardando retirada' : 'Fora há'}</th>
                  <th style={{ ...th, textAlign: 'right' }}>Comprovante</th>
                </tr>
              </thead>
              <tbody>
                {filtrada.map((p) => {
                  const cancelada = !viva(p);
                  const est = cancelada ? ESTILO.Cancelada : estiloDe(p.STATUS);
                  const grupo = grupoDe(p);
                  const dias = diasEntre(p.DATA_ENVIO_MANUTENCAO, p.DATA_RETORNO_COMPRAS);
                  const aberto = detalhe === p.ID;
                  return (
                    <React.Fragment key={p.ID}>
                      <tr
                        onClick={() => setDetalhe(aberto ? null : p.ID)}
                        style={{
                          borderBottom: aberto ? 'none' : '1px solid #f1f5f9', cursor: 'pointer',
                          // A peça FORA leva marca na borda: é a que a loja está esperando.
                          borderLeft: grupo === 'fora' ? '3px solid #1d4ed8' : '3px solid transparent',
                          opacity: cancelada ? 0.55 : 1,
                        }}>
                        <td style={td}><SeloEquipe equipe={p.EQUIPE} /></td>
                        <td style={{ ...td, fontWeight: 800 }}>{p.LOJA}</td>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            {/* ⚠️⚠️ O ÍCONE DIZ SE A LINHA É O EQUIPAMENTO ou uma peça
                                de reposição, e a ordem do teste importa: sem o
                                `TIPO_ITEM` primeiro, um monitor retirado sairia com o
                                ícone de "peça fora da Central" — e a tela diria que
                                alguém precisa COMPRAR um monitor. */}
                            {p.TIPO_ITEM === 'Equipamento'
                              ? <Wrench size={14} color={COR} title="É o próprio equipamento retirado" />
                              : (p.ORIGEM === 'Central'
                                ? <Package size={14} color="#4f46e5" title="Da Central de Pedidos" />
                                : <PackageSearch size={14} color="#b45309" title="Fora da Central" />)}
                            <span style={{ fontWeight: 700, color: '#334155', textDecoration: cancelada ? 'line-through' : 'none' }}>
                              {p.DESCRICAO}
                            </span>
                          </div>
                          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>
                            #{p.ID_REPARO}
                            {/* O caminho especial na linha: interno (roxo) e estoque (azul)
                                ecoam os selos da fila do Compras; o normal não leva marca. */}
                            {p.CAMINHO === 'interno' && <span style={{ color: '#7c3aed', fontWeight: 700 }}> · reparo interno</span>}
                            {p.CAMINHO === 'estoque' && <span style={{ color: '#0369a1', fontWeight: 700 }}> · estoque</span>}
                            {p.NUMERO_SOLICITACAO_SAP ? ` · SAP ${p.NUMERO_SOLICITACAO_SAP}` : ''}
                            {p.NUMERO_NOTA_FISCAL ? ` · NF ${p.NUMERO_NOTA_FISCAL}` : ''}
                            {p.FOTO_URL && <ImgIcon size={12} color="#94a3b8" style={{ marginLeft: 5, verticalAlign: 'middle' }} />}
                          </div>
                        </td>
                        <td style={{ ...td, ...num, textAlign: 'right', fontWeight: 700 }}>{qtd(p.QUANTIDADE)}</td>
                        <td style={td}>
                          <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: est.bg, color: est.color, whiteSpace: 'nowrap' }}>
                            {cancelada ? 'Cancelada' : p.STATUS}
                          </span>
                          {/* O código de rastreio é o que o técnico usa para
                              cobrar o fornecedor — fica visível, não escondido. */}
                          {p.CODIGO_RASTREIO && !cancelada && (
                            <div style={{ ...num, fontSize: 11, color: '#0f766e', marginTop: 4, fontWeight: 700 }}>{p.CODIGO_RASTREIO}</div>
                          )}
                        </td>
                        <td style={{ ...td, ...num, fontSize: 12.5, color: '#475569', whiteSpace: 'nowrap' }}>
                          {p.DATA_ENVIO_MANUTENCAO ? dataBR(p.DATA_ENVIO_MANUTENCAO) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ ...td, ...num, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                          {p.DATA_RETORNO_COMPRAS
                            ? <span style={{ color: '#047857', fontWeight: 700 }}>{dataBR(p.DATA_RETORNO_COMPRAS)}</span>
                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ ...td, ...num, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {/* Na esteira (ainda na loja) a conta é outra: quanto tempo
                              o equipamento esperou da solicitação até o lançamento no
                              SAP — ou até hoje, se ainda não foi lançado. */}
                          {grupo === 'esteira' ? (() => {
                            const espera = esperaRetirada(p);
                            const lancado = !!p.DATA_SOLICITACAO_SAP;
                            return espera === null ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                              <span style={{ fontWeight: espera > 14 ? 800 : 600, color: corTempo(espera) }}>
                                {espera} {espera === 1 ? 'dia' : 'dias'}
                                <span style={{ display: 'block', fontSize: 10.5, fontWeight: 400, color: '#94a3b8' }}>
                                  {lancado ? 'até o SAP' : 'sem SAP ainda'}
                                </span>
                              </span>
                            );
                          })() : (
                          /* ⚠️ Sem data de envio não há conta a fazer — "0 dias"
                              se leria como "acabou de sair". */
                          dias === null ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                            <span style={{
                              fontWeight: (atrasadaNaPrevisao(p) || (grupo === 'fora' && dias > 14)) ? 800 : 600,
                              color: atrasadaNaPrevisao(p) ? '#b91c1c'
                                : (grupo === 'fora' ? corTempo(dias) : '#64748b'),
                            }}>
                              {dias} {dias === 1 ? 'dia' : 'dias'}
                              {/* ⚠️ A legenda diz CONTRA O QUE o número está sendo
                                  lido. "20 dias" sozinho não distingue peça que
                                  voltou, peça no prazo e peça atrasada — e é essa
                                  confusão que a previsão veio resolver. */}
                              <span style={{ display: 'block', fontSize: 10.5, fontWeight: 400, color: atrasadaNaPrevisao(p) ? '#b91c1c' : '#94a3b8' }}>
                                {grupo !== 'fora'
                                  ? (voltouAtrasada(p) ? 'fora do prazo' : 'no total')
                                  : (atrasadaNaPrevisao(p)
                                    ? `${diasAlemDaPrevisao(p)}d além da previsão`
                                    : (p.DATA_PREVISAO_RETORNO ? `prev. ${dataBR(p.DATA_PREVISAO_RETORNO)}` : 'sem previsão'))}
                              </span>
                            </span>
                          ))}
                        </td>

                        {/* ⚠️ `stopPropagation` porque a LINHA INTEIRA abre o
                            detalhe ao clique: sem ele, pedir o comprovante
                            abriria/fecharia o painel junto, e o clique parece
                            ter feito duas coisas. */}
                        <td
                          style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* ⚠️ BOTÃO e não link: a rota é autenticada, e link não
                              manda o token (ver `abrirPdf`). ⚠️ Mostra que está
                              gerando, porque o Puppeteer leva segundos e um botão
                              que não responde faz a pessoa clicar de novo. */}
                          <button
                            type="button"
                            onClick={() => abrirPdf(p.ID_REPARO)}
                            disabled={gerandoPdf === p.ID_REPARO}
                            title="Comprovante da solicitação"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              color: COR, fontWeight: 800, fontSize: 12.5, background: '#fff',
                              border: `1px solid ${COR_BORDA}`, borderRadius: 7, padding: '5px 9px',
                              cursor: gerandoPdf === p.ID_REPARO ? 'default' : 'pointer',
                              opacity: gerandoPdf === p.ID_REPARO ? 0.6 : 1,
                            }}
                          >
                            {gerandoPdf === p.ID_REPARO
                              ? <><Loader2 size={13} /> gerando…</>
                              : <><FileText size={13} /> PDF</>}
                          </button>
                        </td>
                      </tr>

                      {aberto && (
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          {/* ⚠️ 9 colunas desde 10/09 (entraram Equipe e
                              Comprovante). `colSpan` menor que a tabela deixa o
                              painel de detalhe estreito e desalinhado — e o
                              defeito é visual, então nenhum teste o pega. */}
                          <td colSpan={9} style={{ padding: '0 14px 14px 14px', background: '#f8fafc' }}>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', paddingTop: 12 }}>
                              <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                                <p style={{ margin: '0 0 4px 0', fontSize: 11.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                                  {p.EQUIPE === 'TI' ? 'Motivo da retirada' : 'Problema'} — solicitação #{p.ID_REPARO}
                                </p>
                                <p style={{ margin: 0, fontSize: 13.5, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                  {p.DESCRICAO_PROBLEMA}
                                </p>
                                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
                                  Aberta em {dataBR(p.CRIADO_EM)}{p.SOLICITANTE ? ` por ${p.SOLICITANTE}` : ''}
                                  {p.ATUALIZADO_POR ? ` · último passo por ${p.ATUALIZADO_POR}` : ''}
                                </p>
                                {/* Fornecedor do reparo (opcional) — quem vai fazer o reparo;
                                    é da solicitação inteira, não da peça. */}
                                {p.FORNECEDOR_REPARO && (
                                  <p style={{ margin: '6px 0 0 0', fontSize: 12.5, color: '#475569' }}>
                                    <strong>Fornecedor do reparo:</strong> {p.FORNECEDOR_REPARO}
                                  </p>
                                )}
                                {/* Tempo aguardando a retirada: da solicitação ao lançamento
                                    no SAP (ou até hoje, se ainda não foi lançado). */}
                                {grupoDe(p) === 'esteira' && esperaRetirada(p) !== null && (
                                  <p style={{ margin: '6px 0 0 0', fontSize: 12.5, ...num, fontWeight: 700, color: corTempo(esperaRetirada(p)) }}>
                                    Aguardando retirada há {esperaRetirada(p)} {esperaRetirada(p) === 1 ? 'dia' : 'dias'}
                                    {p.DATA_SOLICITACAO_SAP
                                      ? ` (solicitação → SAP em ${dataBR(p.DATA_SOLICITACAO_SAP)})`
                                      : ' (ainda não lançado no SAP)'}
                                  </p>
                                )}
                                {p.SOLIC_CANCELADA_EM && (
                                  <p style={{ margin: '8px 0 0 0', fontSize: 12.5, color: '#475569' }}>
                                    <strong>Solicitação cancelada</strong> em {dataBR(p.SOLIC_CANCELADA_EM)}: {p.SOLIC_CANCELADA_MOTIVO}
                                  </p>
                                )}
                                {p.STATUS === CANCELADA && p.CANCELADA_MOTIVO && (
                                  <p style={{ margin: '8px 0 0 0', fontSize: 12.5, color: '#475569' }}>
                                    <strong>Item cancelado:</strong> {p.CANCELADA_MOTIVO}
                                  </p>
                                )}
                              </div>

                              <div style={{ flex: '0 1 260px', fontSize: 12.5, color: '#475569' }}>
                                <p style={{ margin: '0 0 6px 0', fontSize: 11.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                                  O que já foi registrado
                                </p>
                                <div style={{ ...num, lineHeight: 1.8 }}>
                                  {p.CODIGO && <div>código {p.CODIGO}</div>}
                                  {/* ⚠️ Só nas linhas de EQUIPAMENTO — peça não tem
                                      série, e a coluna vem nula nelas. É este número
                                      que identifica o aparelho físico na volta. */}
                                  {p.NUMERO_SERIE && <div>série {p.NUMERO_SERIE}</div>}
                                  {/* Foto da série: a prova para conferir a digitação
                                      acima. Só nas retiradas de T.I a partir de 21/09. */}
                                  {p.FOTO_SERIE_URL && (
                                    <div style={{ marginTop: 4 }}>
                                      <ImagemSharePoint apiBase={API} url={p.FOTO_SERIE_URL} alt="Foto do número de série" size={120} radius={8} />
                                    </div>
                                  )}
                                  {p.FORNECEDOR && <div>{p.FORNECEDOR}</div>}
                                  {p.NUMERO_SOLICITACAO_SAP && <div>SAP {p.NUMERO_SOLICITACAO_SAP} · {dataBR(p.DATA_SOLICITACAO_SAP)}</div>}
                                  {p.VALOR_UNITARIO != null && (
                                    <div><strong>{moeda(Number(p.VALOR_UNITARIO) * Number(p.QUANTIDADE))}</strong> (un. {moeda(p.VALOR_UNITARIO)})</div>
                                  )}
                                  {p.NUMERO_NOTA_FISCAL && <div>NF {p.NUMERO_NOTA_FISCAL}</div>}
                                  {p.CODIGO_RASTREIO && <div>rastreio {p.CODIGO_RASTREIO}</div>}
                                  {p.DATA_PREVISAO_RETORNO && (
                                    <div>prevista para <strong>{dataBR(p.DATA_PREVISAO_RETORNO)}</strong>
                                      {voltouAtrasada(p) ? ' — voltou fora do prazo' : ''}</div>
                                  )}
                                  {/* ⚠️ BOTÃO, e não `<a href>`: o proxy `/documento` é
                                      rota AUTENTICADA e o navegador NÃO manda o header
                                      `Authorization` ao seguir um link — o clique voltava
                                      401 numa aba nova. Ver `utils/arquivoComToken.js`. */}
                                  {(p.NOTA_FISCAL_URL || p.TXT_URL) && (
                                    <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                                      {p.NOTA_FISCAL_URL && (
                                        <button onClick={() => abrirAnexo(p.NOTA_FISCAL_URL, 'nota-fiscal')}
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: COR, fontWeight: 700, border: 'none', background: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>
                                          <Paperclip size={11} /> NF
                                        </button>
                                      )}
                                      {p.TXT_URL && (
                                        <button onClick={() => abrirAnexo(p.TXT_URL, 'arquivo.txt')}
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: COR, fontWeight: 700, border: 'none', background: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>
                                          <Paperclip size={11} /> TXT
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  {!p.NUMERO_SOLICITACAO_SAP && (
                                    <div style={{ color: '#94a3b8' }}>o Compras ainda não lançou no SAP</div>
                                  )}
                                </div>
                              </div>

                              {p.FOTO_URL && (
                                <ImagemSharePoint apiBase={API} url={p.FOTO_URL} alt="Foto do problema" size={140} radius={8} />
                              )}
                            </div>

                            {/* Cancelar a solicitação INTEIRA — é o que a
                                Manutenção controla. Mover o fluxo, não. */}
                            {!p.SOLIC_CANCELADA_EM && (podeGerir || p.SOLICITANTE_ID === user?.id) && (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                <button onClick={(e) => { e.stopPropagation(); cancelarSolicitacao(p); }}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                                  <Ban size={14} /> Cancelar a solicitação #{p.ID_REPARO}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetiradaAcompanhamento;
